'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { BLENDER_DOWNLOAD_URL, BuildLightmapDialog } from './BuildLightmapDialog';
import type { BlenderCheckResult, LightmapHarvestReadyReason } from '@/app/sdk';
import { sdk } from '@/app/sdk';
import { PRODUCT_NAME } from '@/const';
import { checkBlenderAvailability, editorSave } from '@/editor/core/actions/editorActions';
import { project, sceneStore } from '@/editor/store';
type ResolveFn = (value: boolean) => void;
function toErrorMessage(error?: string): string {
  return error?.trim() || 'Blender was not found. Install Blender or specify its executable path.';
}
export const useBuildLightmapDialog = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [blenderPath, setBlenderPath] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [invalidSelectionMessage, setInvalidSelectionMessage] = useState<string | undefined>();
  const [canStart, setCanStart] = useState(false);
  const [canHarvestBuildData, setCanHarvestBuildData] = useState(false);
  const [harvestReadyReason, setHarvestReadyReason] = useState<LightmapHarvestReadyReason | undefined>();
  const [isCheckingBakedScene, setIsCheckingBakedScene] = useState(false);
  const [targetProjectFolder, setTargetProjectFolder] = useState<string | undefined>();
  const [targetScenePath, setTargetScenePath] = useState<string | undefined>();
  const pendingResolveRef = useRef<ResolveFn | null>(null);
  const resolvePending = useCallback((value: boolean) => {
    pendingResolveRef.current?.(value);
    pendingResolveRef.current = null;
  }, []);
  const applyCheckResult = useCallback((result: BlenderCheckResult, userSelected = false): BlenderCheckResult => {
    if (result.available && result.executable) {
      setBlenderPath(result.executable);
      setErrorMessage(undefined);
      setInvalidSelectionMessage(undefined);
      setCanStart(true);
      return result;
    }
    setCanStart(false);
    if (userSelected) {
      setErrorMessage(undefined);
      setInvalidSelectionMessage(toErrorMessage(result.error));
    } else {
      setErrorMessage(toErrorMessage(result.error));
      setInvalidSelectionMessage(undefined);
    }
    return result;
  }, []);
  const refreshHarvestReady = useCallback(async (): Promise<boolean> => {
    const checkHarvest = sdk?.tools.checkLightmapHarvestReady;
    if (!checkHarvest || !targetProjectFolder || !targetScenePath) {
      setCanHarvestBuildData(false);
      setHarvestReadyReason(undefined);
      return false;
    }
    try {
      const result_0 = await checkHarvest(targetProjectFolder, targetScenePath);
      setCanHarvestBuildData(result_0.ready);
      setHarvestReadyReason(result_0.reason);
      return result_0.ready;
    } catch {
      setCanHarvestBuildData(false);
      setHarvestReadyReason(undefined);
      return false;
    }
  }, [targetProjectFolder, targetScenePath]);
  const runBlenderCheck = useCallback(async (optionalPath?: string, userSelected_0 = false): Promise<BlenderCheckResult> => {
    setIsBusy(true);
    try {
      const result_1 = await checkBlenderAvailability(optionalPath, userSelected_0);
      return applyCheckResult(result_1, userSelected_0);
    } catch (error) {
      const fallbackMessage = error instanceof Error ? error.message : String(error);
      setCanStart(false);
      const failed: BlenderCheckResult = {
        available: false,
        error: fallbackMessage
      };
      if (userSelected_0) {
        setInvalidSelectionMessage(toErrorMessage(fallbackMessage));
      } else {
        setErrorMessage(toErrorMessage(fallbackMessage));
      }
      return failed;
    } finally {
      setIsBusy(false);
    }
  }, [applyCheckResult]);
  const closeDialog = useCallback((confirmed: boolean) => {
    setIsDialogOpen(false);
    setCanStart(false);
    setErrorMessage(undefined);
    setInvalidSelectionMessage(undefined);
    resolvePending(confirmed);
  }, [resolvePending]);

  /** Opens the bake confirmation dialog; resolves true when the user clicks Start with a valid Blender path. */
  const confirmBuildLightmap = useCallback((projectFolder?: string, scenePath?: string | null): Promise<boolean> => {
    setTargetProjectFolder(projectFolder);
    setTargetScenePath(scenePath ?? undefined);
    setErrorMessage(undefined);
    setInvalidSelectionMessage(undefined);
    setCanStart(false);
    setCanHarvestBuildData(false);
    setHarvestReadyReason(undefined);
    setIsBusy(true);
    setIsDialogOpen(true);
    return new Promise(resolve => {
      pendingResolveRef.current = resolve;
    });
  }, []);
  const handleCancel = useCallback(() => {
    closeDialog(false);
  }, [closeDialog]);
  const handleInstall = useCallback(async () => {
    try {
      await sdk?.os.openUrl(BLENDER_DOWNLOAD_URL);
    } catch (error_0) {
      console.error('[BuildLightmap] Failed to open Blender download page:', error_0);
    }
  }, []);
  const handleBrowseForBlender = useCallback(async () => {
    const chooseBlender = sdk?.os.chooseBlenderExecutable;
    if (!chooseBlender) {
      setErrorMessage(`File picker is not available outside the ${PRODUCT_NAME} desktop app.`);
      return;
    }
    const picked = await chooseBlender('Select Blender Executable');
    if (!picked) {
      return;
    }
    setBlenderPath(picked);
    const result_2 = await runBlenderCheck(picked, true);
    if (result_2.available && result_2.executable) {
      try {
        await sdk?.app.setState('app.blenderExecutablePath', result_2.executable);
        setBlenderPath(result_2.executable);
      } catch (error_1) {
        console.error('[BuildLightmap] Failed to save Blender path:', error_1);
      }
    }
  }, [runBlenderCheck]);
  const handleBakeInBlender = useCallback(async () => {
    if (!targetProjectFolder || !targetScenePath) {
      setErrorMessage('Open a project and scene before baking lightmaps in Blender.');
      return;
    }
    const bakeInBlender = sdk?.tools.bakeLightmapInBlender;
    if (!bakeInBlender) {
      setErrorMessage(`Bake In Blender requires the ${PRODUCT_NAME} desktop app.`);
      return;
    }
    if (!canStart) {
      setErrorMessage('Confirm Blender is available before baking in Blender.');
      return;
    }
    setCanHarvestBuildData(false);
    setHarvestReadyReason(undefined);
    setIsBusy(true);
    try {
      const saved = await editorSave(false, true);
      if (!saved) {
        return;
      }
      const result_3 = await bakeInBlender(targetProjectFolder, targetScenePath);
      if (!result_3.success) {
        setErrorMessage(result_3.message);
      } else {
        setErrorMessage(undefined);
        toast.info(result_3.message);
      }
    } catch (error_2) {
      setErrorMessage(error_2 instanceof Error ? error_2.message : String(error_2));
    } finally {
      setIsBusy(false);
    }
  }, [canStart, targetProjectFolder, targetScenePath]);
  const handleCheckForBakedScene = useCallback(async () => {
    if (!targetProjectFolder || !targetScenePath) {
      setErrorMessage('Open a project and scene before checking for a baked scene.');
      return;
    }
    if (!sdk?.tools.checkLightmapHarvestReady) {
      setErrorMessage(`Check for Baked Scene requires the ${PRODUCT_NAME} desktop app.`);
      return;
    }
    setIsCheckingBakedScene(true);
    setErrorMessage(undefined);
    try {
      await refreshHarvestReady();
    } catch (error_3) {
      setErrorMessage(error_3 instanceof Error ? error_3.message : String(error_3));
    } finally {
      setIsCheckingBakedScene(false);
    }
  }, [refreshHarvestReady, targetProjectFolder, targetScenePath]);
  const handleImportBakedLightmaps = useCallback(async () => {
    if (!targetProjectFolder || !targetScenePath) {
      setErrorMessage('Open a project and scene before importing baked lightmaps.');
      return;
    }
    const harvest = sdk?.tools.harvestLightmap;
    if (!harvest) {
      setErrorMessage(`Import Baked Lightmaps requires the ${PRODUCT_NAME} desktop app.`);
      return;
    }
    setIsBusy(true);
    try {
      const result_4 = await harvest(targetProjectFolder, targetScenePath);
      if (!result_4.success) {
        setErrorMessage(result_4.message);
        return;
      }
      setErrorMessage(undefined);
      if (sceneStore.world && project.currentScene) {
        await ENGINE.tryApplyLightmapsAfterSceneReady(sceneStore.world, project.currentScene);
      }
    } catch (error_4) {
      setErrorMessage(error_4 instanceof Error ? error_4.message : String(error_4));
    } finally {
      setIsBusy(false);
      await refreshHarvestReady();
    }
  }, [refreshHarvestReady, targetProjectFolder, targetScenePath]);
  const handleResetBlenderPath = useCallback(async () => {
    setBlenderPath(undefined);
    setErrorMessage(undefined);
    try {
      await sdk?.app.setState('app.blenderExecutablePath', undefined);
    } catch (error_5) {
      console.error('[BuildLightmap] Failed to clear saved Blender path:', error_5);
    }
    await runBlenderCheck();
  }, [runBlenderCheck]);
  const handleStart = useCallback(() => {
    if (!canStart) {
      return;
    }
    closeDialog(true);
  }, [canStart, closeDialog]);
  useEffect(() => {
    if (!isDialogOpen) {
      return;
    }
    void (async () => {
      const saved_0 = (await sdk?.app.getState('app.blenderExecutablePath')) as string | undefined;
      if (saved_0?.trim()) {
        setBlenderPath(saved_0);
      }
      await runBlenderCheck();
      if (!sdk?.tools.checkLightmapHarvestReady || !targetProjectFolder || !targetScenePath) {
        return;
      }
      setIsCheckingBakedScene(true);
      try {
        await refreshHarvestReady();
      } finally {
        setIsCheckingBakedScene(false);
      }
    })();
  }, [isDialogOpen, refreshHarvestReady, runBlenderCheck, targetProjectFolder, targetScenePath]);
  useEffect(() => {
    return () => {
      if (pendingResolveRef.current && isDialogOpen) {
        resolvePending(false);
      }
    };
  }, [isDialogOpen, resolvePending]);
  const buildLightmapDialog = <BuildLightmapDialog open={isDialogOpen} blenderPath={blenderPath} errorMessage={errorMessage} invalidSelectionMessage={invalidSelectionMessage} canStart={canStart} canBakeInBlender={canStart && Boolean(targetProjectFolder && targetScenePath)} canCheckBakedScene={Boolean(targetProjectFolder && targetScenePath)} canHarvestBuildData={canHarvestBuildData} harvestReadyReason={harvestReadyReason} isBusy={isBusy} isCheckingBakedScene={isCheckingBakedScene} showInstallBlender={isDialogOpen && !isBusy && !isCheckingBakedScene && !canStart} onCancel={handleCancel} onInstall={() => {
    void handleInstall();
  }} onBrowseForBlender={() => {
    void handleBrowseForBlender();
  }} onBakeInBlender={() => {
    void handleBakeInBlender();
  }} onCheckForBakedScene={() => {
    void handleCheckForBakedScene();
  }} onImportBakedLightmaps={() => {
    void handleImportBakedLightmaps();
  }} onResetBlenderPath={() => {
    void handleResetBlenderPath();
  }} onDismissInvalidSelection={() => {
    setInvalidSelectionMessage(undefined);
  }} onStart={handleStart} />;
  return {
    confirmBuildLightmap,
    buildLightmapDialog
  };
};